import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Save,
  Plus,
  X,
  Check,
  Calendar,
  ExternalLink,
  Pencil,
  Trash2,
  Settings2,
  Download,
  Printer,
} from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { DossierEditor } from '../components/editor';
import { apiGet, apiPatch } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { tiptapToMarkdown, downloadMarkdown, printAsPdf } from '../lib/export';

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface ReferenceDocDetail {
  id: string;
  title: string;
  annotations: string | null;
  snapshot_date: string | null;
  tag_ids: string[];
  scope_ids: string[];
  tags: Tag[];
  created_at: string;
  updated_at: string;
}

interface AggregatedContentItem {
  note_tag_id: string;
  meeting_instance_id: string;
  meeting_date: string;
  series_id: string;
  series_name: string;
  tagged_text: string;
  tag_name: string;
  tag_id: string;
  tag_color: string;
  block_id: string | null;
}

// Annotation: a user-written rich text section pinned between aggregated items
interface Annotation {
  id: string;
  after_item_id: string | null; // null = at the top, or the note_tag_id it comes after
  content: JSONContent;
}

export function ReferenceDocDetailPage() {
  const { docId } = useParams<{ docId: string }>();
  const toast = useToast();
  const [doc, setDoc] = useState<ReferenceDocDetail | null>(null);
  const [aggregated, setAggregated] = useState<AggregatedContentItem[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showTagEditor, setShowTagEditor] = useState(false);

  async function load() {
    if (!docId) return;
    setLoading(true);
    try {
      const [docData, contentData] = await Promise.all([
        apiGet<ReferenceDocDetail>(`/reference-docs/${docId}`),
        apiGet<AggregatedContentItem[]>(`/reference-docs/${docId}/content`),
      ]);
      setDoc(docData);
      setAggregated(contentData);
      setTitleDraft(docData.title);

      // Parse existing annotations from JSON
      if (docData.annotations) {
        try {
          const parsed = JSON.parse(docData.annotations) as Annotation[];
          setAnnotations(parsed);
        } catch {
          setAnnotations([]);
        }
      } else {
        setAnnotations([]);
      }
    } catch (err) {
      console.error('Failed to load reference doc:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [docId]);

  const saveAnnotations = useCallback(async (updatedAnnotations: Annotation[]) => {
    if (!docId) return;
    setSaving(true);
    try {
      await apiPatch(`/reference-docs/${docId}`, {
        annotations: JSON.stringify(updatedAnnotations),
      });
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save annotations:', err);
      toast.error('Failed to save annotations');
    } finally {
      setSaving(false);
    }
  }, [docId]);

  async function handleTitleSave() {
    if (!docId || !titleDraft.trim()) return;
    setSaving(true);
    try {
      const updated = await apiPatch<ReferenceDocDetail>(`/reference-docs/${docId}`, {
        title: titleDraft.trim(),
      });
      setDoc(updated);
      setEditingTitle(false);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save title:', err);
      toast.error('Failed to save title');
    } finally {
      setSaving(false);
    }
  }

  async function handleTagsUpdate(tagIds: string[]) {
    if (!docId) return;
    setSaving(true);
    try {
      const updated = await apiPatch<ReferenceDocDetail>(`/reference-docs/${docId}`, {
        tag_ids: tagIds,
      });
      setDoc(updated);
      // Reload aggregated content with new tags
      const contentData = await apiGet<AggregatedContentItem[]>(`/reference-docs/${docId}/content`);
      setAggregated(contentData);
      setShowTagEditor(false);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to update tags:', err);
      toast.error('Failed to update tags');
    } finally {
      setSaving(false);
    }
  }

  function addAnnotation(afterItemId: string | null) {
    const newAnnotation: Annotation = {
      id: crypto.randomUUID(),
      after_item_id: afterItemId,
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
    };
    const updated = [...annotations, newAnnotation];
    setAnnotations(updated);
    saveAnnotations(updated);
  }

  function updateAnnotation(id: string, content: JSONContent) {
    const updated = annotations.map((a) =>
      a.id === id ? { ...a, content } : a
    );
    setAnnotations(updated);
    saveAnnotations(updated);
  }

  function deleteAnnotation(id: string) {
    const updated = annotations.filter((a) => a.id !== id);
    setAnnotations(updated);
    saveAnnotations(updated);
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!doc) {
    return <div className="h-full overflow-auto p-6"><div className="text-text-muted text-center py-16">Reference document not found.</div></div>;
  }

  // Build the interleaved view: annotations placed after specific items
  const annotationsByPosition = new Map<string | null, Annotation[]>();
  for (const ann of annotations) {
    const key = ann.after_item_id;
    if (!annotationsByPosition.has(key)) annotationsByPosition.set(key, []);
    annotationsByPosition.get(key)!.push(ann);
  }

  // Group aggregated content by meeting
  const meetingGroups = groupByMeeting(aggregated);

  return (
    <div className="h-full overflow-auto p-6">
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/references"
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronLeft size={16} />
          Reference Documents
        </Link>
        <div className="flex items-center gap-2">
          {lastSaved && (
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <Save size={12} />
              Saved at {lastSaved}
            </span>
          )}
          {saving && (
            <span className="text-xs text-accent">Saving...</span>
          )}
          <button
            onClick={() => {
              // Build markdown from annotations + aggregated content
              let md = `# ${doc!.title}\n\n`;
              for (const ann of annotations) {
                md += tiptapToMarkdown(ann.content) + '\n';
              }
              for (const item of aggregated) {
                md += `- ${item.tagged_text} [${item.tag_name}]\n`;
              }
              downloadMarkdown(md, doc!.title);
            }}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors no-print"
            title="Export as Markdown"
          >
            <Download size={14} />
          </button>
          <button
            onClick={printAsPdf}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors no-print"
            title="Print / Save as PDF"
          >
            <Printer size={14} />
          </button>
        </div>
      </div>

      {/* Header: editable title */}
      <div className="mb-4">
        {editingTitle ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSave();
                if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(doc.title); }
              }}
              autoFocus
              className="text-2xl font-bold bg-bg-input border border-border rounded-md px-2 py-1 text-text-primary focus:outline-none focus:border-accent flex-1"
            />
            <button onClick={handleTitleSave} className="p-1 text-success hover:text-success/80">
              <Check size={20} />
            </button>
            <button onClick={() => { setEditingTitle(false); setTitleDraft(doc.title); }} className="p-1 text-text-muted hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <h1 className="text-2xl font-bold text-text-primary">{doc.title}</h1>
            <button
              onClick={() => setEditingTitle(true)}
              className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-text-primary transition-opacity"
            >
              <Pencil size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Tag filters */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <span className="text-xs text-text-muted uppercase tracking-wider">Filters:</span>
        {doc.tags.map((tag) => (
          <span
            key={tag.id}
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: tag.color + '20', color: tag.color }}
          >
            {tag.name}
          </span>
        ))}
        {doc.tags.length === 0 && (
          <span className="text-xs text-text-muted italic">No tag filters set</span>
        )}
        <button
          onClick={() => setShowTagEditor(true)}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-accent px-2 py-0.5 rounded-md hover:bg-bg-hover transition-colors"
        >
          <Settings2 size={12} /> Edit Tags
        </button>
      </div>

      {/* Content area */}
      <div className="space-y-3">
        {/* Annotations at the top (before any aggregated content) */}
        <AddAnnotationButton onClick={() => addAnnotation(null)} label="Add note at top" />
        {(annotationsByPosition.get(null) ?? []).map((ann) => (
          <AnnotationBlock
            key={ann.id}
            annotation={ann}
            onUpdate={updateAnnotation}
            onDelete={deleteAnnotation}
          />
        ))}

        {/* Interleaved aggregated content and annotations */}
        {aggregated.length === 0 && annotations.length === 0 ? (
          <div className="text-center py-12 text-text-muted">
            <p className="text-sm">
              {doc.tags.length === 0
                ? 'Add tag filters to start aggregating content.'
                : 'No tagged content found. Tag content in your meeting notes to see it here.'}
            </p>
          </div>
        ) : (
          meetingGroups.map((group) => (
            <div key={group.key} className="space-y-2">
              {/* Meeting group header */}
              <div className="flex items-center gap-2 pt-2">
                <Calendar size={14} className="text-text-muted" />
                <span className="text-xs font-medium text-text-secondary">
                  {formatDate(group.meeting_date)}
                </span>
                <span className="text-xs text-text-muted">&mdash;</span>
                <Link
                  to={`/meetings/${group.series_id}/${group.meeting_instance_id}`}
                  className="text-xs text-accent hover:text-accent-hover flex items-center gap-1"
                >
                  {group.series_name} <ExternalLink size={10} />
                </Link>
              </div>

              {/* Items within this meeting */}
              {group.items.map((item) => (
                <div key={item.note_tag_id}>
                  <AggregatedCard item={item} />
                  <AddAnnotationButton onClick={() => addAnnotation(item.note_tag_id)} />
                  {(annotationsByPosition.get(item.note_tag_id) ?? []).map((ann) => (
                    <AnnotationBlock
                      key={ann.id}
                      annotation={ann}
                      onUpdate={updateAnnotation}
                      onDelete={deleteAnnotation}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {showTagEditor && (
        <TagEditorModal
          currentTagIds={doc.tag_ids}
          onSave={handleTagsUpdate}
          onClose={() => setShowTagEditor(false)}
        />
      )}
    </div>
    </div>
  );
}

// -- Subcomponents --

function AggregatedCard({ item }: { item: AggregatedContentItem }) {
  return (
    <div className="bg-bg-card border border-border rounded-lg px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-text-primary whitespace-pre-wrap">{item.tagged_text}</p>
        </div>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
        >
          {item.tag_name}
        </span>
      </div>
    </div>
  );
}

function AnnotationBlock({
  annotation,
  onUpdate,
  onDelete,
}: {
  annotation: Annotation;
  onUpdate: (id: string, content: JSONContent) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="relative group">
      <div className="border-l-2 border-accent/30 pl-3 ml-2">
        <DossierEditor
          content={annotation.content}
          onUpdate={(content) => onUpdate(annotation.id, content)}
          editable={true}
          placeholder="Write a note..."
        />
      </div>
      <button
        onClick={() => onDelete(annotation.id)}
        className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 p-1 bg-bg-surface border border-border rounded text-text-muted hover:text-danger transition-all"
        title="Remove annotation"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function AddAnnotationButton({ onClick, label }: { onClick: () => void; label?: string }) {
  return (
    <div className="flex justify-center py-1">
      <button
        onClick={onClick}
        className="flex items-center gap-1 text-xs text-text-muted hover:text-accent px-2 py-0.5 rounded hover:bg-bg-hover transition-colors opacity-0 hover:opacity-100 focus:opacity-100"
      >
        <Plus size={12} /> {label ?? 'Add note'}
      </button>
    </div>
  );
}

function TagEditorModal({
  currentTagIds,
  onSave,
  onClose,
}: {
  currentTagIds: string[];
  onSave: (tagIds: string[]) => void;
  onClose: () => void;
}) {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set(currentTagIds));
  const [loadingTags, setLoadingTags] = useState(true);

  useEffect(() => {
    apiGet<Tag[]>('/tags')
      .then(setAllTags)
      .catch(console.error)
      .finally(() => setLoadingTags(false));
  }, []);

  function toggleTag(tagId: string) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">Edit Tag Filters</h2>
        <p className="text-xs text-text-muted mb-3">
          Select which tags to aggregate content from.
        </p>
        {loadingTags ? (
          <div className="text-xs text-text-muted">Loading tags...</div>
        ) : allTags.length === 0 ? (
          <div className="text-xs text-text-muted">No tags available.</div>
        ) : (
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {allTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => toggleTag(tag.id)}
                className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
                  selectedTagIds.has(tag.id)
                    ? 'bg-accent/10 text-text-primary'
                    : 'text-text-secondary hover:bg-bg-hover'
                }`}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="flex-1">{tag.name}</span>
                {selectedTagIds.has(tag.id) && (
                  <Check size={14} className="text-accent" />
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">
            Cancel
          </button>
          <button
            onClick={() => onSave(Array.from(selectedTagIds))}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// -- Helpers --

interface MeetingGroup {
  key: string;
  meeting_instance_id: string;
  meeting_date: string;
  series_name: string;
  series_id: string;
  items: AggregatedContentItem[];
}

function groupByMeeting(items: AggregatedContentItem[]): MeetingGroup[] {
  const groups = new Map<string, MeetingGroup>();
  for (const item of items) {
    const key = item.meeting_instance_id;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        meeting_instance_id: item.meeting_instance_id,
        meeting_date: item.meeting_date,
        series_name: item.series_name,
        series_id: item.series_id,
        items: [],
      });
    }
    groups.get(key)!.items.push(item);
  }
  return Array.from(groups.values());
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
